import React from "react";
import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { useLang } from "@/i18n";

const FAQ = {
  nl: {
    title: "Veelgestelde vragen",
    subtitle: "Alles wat u wilt weten voordat u een schoonmaker boekt.",
    items: [
      {
        q: "Moet ik zelf schoonmaakmiddelen en materialen regelen?",
        a: "Nee. Onze schoonmakers nemen alle benodigde schoonmaakmiddelen en materialen mee. We vragen u alleen om een stofzuiger en dweil beschikbaar te stellen, zodat deze geschikt zijn voor uw vloeren.",
      },
      {
        q: "Kan ik een afspraak wijzigen of annuleren?",
        a: "Ja. U kunt uw afspraak kosteloos wijzigen of annuleren, mits u dit minimaal 24 uur van tevoren aan ons doorgeeft.",
      },
      {
        q: "Zijn jullie schoonmakers gescreend?",
        a: "Ja. Al onze schoonmakers worden zorgvuldig geselecteerd, gescreend en gecontroleerd voordat zij bij klanten aan de slag gaan.",
      },
      {
        q: "Hoe betaal ik?",
        a: "Na de schoonmaak ontvangt u een factuur. Betaling verloopt eenvoudig via de betaalmethode die vooraf met u is afgesproken.",
      },
    ],
  },
  en: {
    title: "Frequently asked questions",
    subtitle: "Everything you want to know before booking a cleaner.",
    items: [
      {
        q: "Do I need to provide cleaning supplies and materials?",
        a: "No. Our cleaners bring all the required cleaning products and materials. We only ask you to provide a vacuum cleaner and a mop, so they are suitable for your floors.",
      },
      {
        q: "Can I change or cancel an appointment?",
        a: "Yes. You can change or cancel your appointment free of charge, as long as you let us know at least 24 hours in advance.",
      },
      {
        q: "Are your cleaners screened?",
        a: "Yes. All our cleaners are carefully selected, screened and vetted before they start working with clients.",
      },
      {
        q: "How do I pay?",
        a: "After the cleaning you receive an invoice. Payment is simple, via the payment method agreed with you in advance.",
      },
    ],
  },
};

const FaqSection = () => {
  const { lang } = useLang();
  const f = FAQ[lang];

  return (
    <section id="faq" className="bg-white px-5 py-24 md:px-8">
      <div className="mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-700">
            <HelpCircle className="h-3.5 w-3.5" /> FAQ
          </span>
          <h2 className="mt-6 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{f.title}</h2>
          <p className="mt-4 text-lg text-slate-600">{f.subtitle}</p>
        </motion.div>

        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {f.items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/60 px-6 shadow-sm"
            >
              <AccordionTrigger className="py-5 text-left font-heading text-base font-semibold text-slate-900 hover:no-underline sm:text-lg">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-base leading-relaxed text-slate-600">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FaqSection;
